'use client';

import React, { useState, useCallback } from 'react';
import { Rate, Image, Button, message } from 'antd';
import { FaThumbsUp, FaUser, FaCheckCircle } from 'react-icons/fa';
import { useReviewerAuth } from '../contexts/ReviewerAuthContext';
import { toggleHelpful } from '../utils/reviewerApi';
import { formatReviewerName } from '../utils/reviewerName';
import type { Review } from '../types';

interface ReviewCardProps {
  review: Review;
  onHelpfulToggle?: (
    reviewId: string,
    newCount: number,
    isHelpful: boolean,
  ) => void;
}

/**
 * Format an ISO date string to a human-friendly visit date badge,
 * e.g. "Visited Dec 2025".
 */
const formatVisitDate = (dateStr: string): string => {
  try {
    const date = new Date(dateStr);
    const month = date.toLocaleString('en-AU', { month: 'short' });
    const year = date.getFullYear();
    return `Visited ${month} ${year}`;
  } catch {
    return '';
  }
};

/**
 * Format an ISO date string to a relative or short absolute form,
 * e.g. "2 days ago" or "14 Jan 2025".
 */
const formatRelativeDate = (dateStr: string): string => {
  try {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;
    }
    if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return `${months} ${months === 1 ? 'month' : 'months'} ago`;
    }
    return date.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '';
  }
};

/**
 * Build initials from a display name (up to 2 characters).
 */
const getInitials = (name: string): string => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

/** Sentiment badge derived from star rating */
const getSentimentBadge = (rating: number): { label: string; bg: string; text: string } => {
  if (rating >= 4) return { label: 'Positive', bg: 'bg-green-50', text: 'text-green-700' };
  if (rating === 3) return { label: 'Neutral', bg: 'bg-gray-100', text: 'text-gray-600' };
  return { label: 'Negative', bg: 'bg-red-50', text: 'text-red-700' };
};

const TRUNCATE_LENGTH = 300;

const ReviewCard: React.FC<ReviewCardProps> = ({ review, onHelpfulToggle }) => {
  const { isLoggedIn } = useReviewerAuth();
  const [expanded, setExpanded] = useState(false);
  const [helpfulCount, setHelpfulCount] = useState(review.helpful_count);
  const [isHelpful, setIsHelpful] = useState(review.is_helpful_by_me ?? false);
  const [toggling, setToggling] = useState(false);

  const shouldTruncate = review.content.length > TRUNCATE_LENGTH;
  const displayedContent =
    shouldTruncate && !expanded
      ? review.content.slice(0, TRUNCATE_LENGTH) + '...'
      : review.content;

  // ── Helpful toggle handler ──────────────────────────────────
  const handleHelpfulClick = useCallback(async () => {
    if (toggling) return;

    setToggling(true);
    try {
      const res = await toggleHelpful(review.id);
      const newCount = res.helpful_count ?? (isHelpful ? helpfulCount - 1 : helpfulCount + 1);
      const newIsHelpful = res.is_helpful ?? !isHelpful;

      setHelpfulCount(newCount);
      setIsHelpful(newIsHelpful);
      onHelpfulToggle?.(review.id, newCount, newIsHelpful);
    } catch {
      message.error('Could not update helpful vote. Please try again.');
    } finally {
      setToggling(false);
    }
  }, [isLoggedIn, toggling, review.id, isHelpful, helpfulCount, onHelpfulToggle]);

  // ── Render ──────────────────────────────────────────────────
  return (
    <div className="py-5 border-b border-gray-100 last:border-b-0">
      {/* ── Reviewer header ────────────────────────────────── */}
      <div className="flex items-start gap-3 mb-3">
        {/* Avatar */}
        {review.reviewer.profile_image ? (
          <img
            src={review.reviewer.profile_image}
            alt={review.reviewer.display_name}
            className="w-10 h-10 rounded-full object-cover shrink-0"
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-[#e3f2fd] text-[#1558b0] flex items-center justify-center text-sm font-semibold shrink-0">
            {getInitials(review.reviewer.display_name)}
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm text-gray-900">
              {formatReviewerName(review.reviewer.display_name)}
            </span>
            {review.reviewer.review_count > 0 && (
              <span className="text-[11px] text-gray-400 flex items-center gap-0.5">
                <FaUser className="text-[9px]" />
                {review.reviewer.review_count}{' '}
                {review.reviewer.review_count === 1 ? 'review' : 'reviews'}
              </span>
            )}
          </div>

          {/* Star rating + visit date + posted date */}
          <div className="flex items-center gap-2 flex-wrap mt-0.5">
            <Rate
              disabled
              allowHalf
              value={review.overall_rating}
              className="!text-amber-400 !text-xs"
            />
            {(() => {
              const badge = getSentimentBadge(review.overall_rating);
              return (
                <span className={`text-[11px] ${badge.bg} ${badge.text} px-1.5 py-0.5 rounded font-medium`}>
                  {badge.label}
                </span>
              );
            })()}
            {review.visit_date && (
              <span className="text-[11px] bg-green-50 text-green-700 px-1.5 py-0.5 rounded font-medium">
                {formatVisitDate(review.visit_date)}
              </span>
            )}
            <span className="text-[11px] text-gray-400">
              {formatRelativeDate(review.created_at)}
            </span>
          </div>
        </div>
      </div>

      {/* ── Review body ────────────────────────────────────── */}
      {review.title && (
        <h4 className="font-bold text-sm text-gray-900 mb-1">
          {review.title}
        </h4>
      )}

      <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
        {displayedContent}
        {shouldTruncate && (
          <button
            type="button"
            onClick={() => setExpanded((prev) => !prev)}
            className="ml-1 text-[#1558b0] hover:text-[#123f7d] text-sm font-medium focus:outline-none"
          >
            {expanded ? 'Show less' : 'Read more'}
          </button>
        )}
      </p>

      {/* ── Photo thumbnails ───────────────────────────────── */}
      {review.images && review.images.length > 0 && (
        <div className="mt-3">
          <Image.PreviewGroup>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {review.images.map((img) => {
                const imgSrc = img.image_src?.startsWith('/')
                  ? `https://jola-888.imgix.net${img.image_src}?w=240&h=240&fit=crop&auto=format`
                  : img.image_src;
                const previewSrc = img.image_src?.startsWith('/')
                  ? `https://jola-888.imgix.net${img.image_src}?w=1200&auto=format`
                  : img.image_src;
                return (
                  <Image
                    key={img.id}
                    src={imgSrc}
                    preview={{ src: previewSrc }}
                    alt="Review photo"
                    width={120}
                    height={120}
                    className="!rounded-lg object-cover shrink-0 cursor-pointer"
                    style={{ objectFit: 'cover' }}
                  />
                );
              })}
            </div>
          </Image.PreviewGroup>
        </div>
      )}

      {/* ── Helpful button ─────────────────────────────────── */}
      <div className="mt-3 flex items-center gap-3">
        <Button
          type="text"
          size="small"
          loading={toggling}
          onClick={handleHelpfulClick}
          className={`!flex !items-center !gap-1.5 !text-xs !rounded-full !px-3 ${
            isHelpful
              ? '!text-[#1558b0] !bg-[#e3f2fd] !font-semibold'
              : '!text-gray-500 hover:!text-gray-700 hover:!bg-gray-50'
          }`}
          icon={
            <FaThumbsUp
              className={`text-[11px] ${isHelpful ? 'text-[#1558b0]' : 'text-gray-400'}`}
            />
          }
        >
          Helpful{helpfulCount > 0 ? ` (${helpfulCount})` : ''}
        </Button>

      </div>

      {/* ── Owner response ─────────────────────────────────── */}
      {review.owner_response && (
        <div className="mt-4 ml-6 pl-4 border-l-2 border-green-200 bg-green-50/50 rounded-r-lg py-3 pr-4">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
              <FaCheckCircle className="text-[9px]" />
              Owner
            </span>
            <span className="text-[11px] text-gray-400">
              {formatRelativeDate(review.owner_response.responded_at)}
            </span>
          </div>
          <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
            {review.owner_response.content}
          </p>
        </div>
      )}
    </div>
  );
};

export default ReviewCard;
